/**
 * @file DataQueryModal.js
 * @version 2.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-11
 * @modified 2026-09-11
 * @description Ask the Data — a dark overlay that starts as a prompt and grows
 *              into an answer.
 *
 *              WHY THIS IS NOT THE SHARED Modal
 *
 *              Everything else in the app is a panel in the app's own theme,
 *              and that is right for a screen you navigated to. This is not a
 *              screen. It opens over whatever you were doing, answers, and
 *              leaves — so it reads as a separate thing sitting on top rather
 *              than another page. That means its own dark shell, borrowed from
 *              the logger widget's terminal palette, and its own sizing: Modal
 *              has fixed sizes and this one has to grow.
 *
 *              It uses its own `.dq-overlay` class rather than `.modal-overlay`
 *              for a concrete reason as well as an aesthetic one: index.js
 *              installs a capture-phase click guard that disables
 *              backdrop-click-to-close for `.modal-overlay` system-wide.
 *              BlendCommandPalette sidesteps it the same way.
 *
 *              THE THREE SIZES ARE THE POINT
 *
 *                ask      just the prompt — small, because an empty box that
 *                         fills the screen is asking you to be impressed before
 *                         you have typed anything
 *                reading  the prompt plus what it understood
 *                answer   plus the figures and the rows
 *
 *              It grows through them as it works. Height animates via
 *              grid-template-rows 0fr -> 1fr, which is the only way to
 *              transition to an automatic height — the same technique the
 *              maintenance banner uses.
 *
 *              WHY THE READING ARRIVES FIRST
 *
 *              Two requests, not one. /plan returns what the question was
 *              understood to mean; /run then answers it. A query can take
 *              seconds, and the reading is what lets somebody notice they were
 *              misunderstood — so it belongs on screen WHILE the query runs,
 *              not after it. The client never holds or sends SQL; /run takes an
 *              opaque token.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { API_URL } from '../config';
import './DataQueryModal.css';

/** Numbers right, everything else left — decided per column, not per cell. */
function isNumericColumn(rows, key) {
  let seen = 0;
  for (const r of rows) {
    const v = r[key];
    if (v === null || v === undefined || v === '') continue;
    if (typeof v === 'number') { seen += 1; continue; }
    // Postgres numerics arrive as strings; a date must not be mistaken for one.
    if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v)) { seen += 1; continue; }
    return false;
  }
  return seen > 0;
}

function renderCell(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** A section that animates open from nothing to its natural height. */
function Reveal({ open, children, className = '' }) {
  return (
    <div className={`dq-reveal ${open ? 'open' : ''} ${className}`} aria-hidden={!open}>
      <div className="dq-reveal-inner">{children}</div>
    </div>
  );
}

function ResultTable({ columns, rows }) {
  if (!columns?.length || !rows?.length) return null;
  const numeric = {};
  for (const c of columns) numeric[c] = isNumericColumn(rows, c);
  return (
    <div className="dq-table-wrap">
      <table className="dq-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} className={numeric[c] ? 'num' : ''}>{c.replace(/_/g, ' ')}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c} className={numeric[c] ? 'num' : ''}>{renderCell(row[c])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Who you are asking, this time.
 *
 * Drawn once when the overlay mounts and held for as long as it is open — the
 * placeholder must not change under someone mid-sentence, which is what would
 * happen if this were re-evaluated on render. Dashboard unmounts the component
 * on close, so the next open draws again.
 */
const ASKEES = ['Clem', 'Kim', 'Tim', 'Tam', 'Clam', 'Clement', 'Carmen'];

const CONFIDENCE = {
  high: { label: 'confident', cls: 'dq-conf-high' },
  medium: { label: 'fairly sure', cls: 'dq-conf-medium' },
  low: { label: 'not sure', cls: 'dq-conf-low' }
};

export default function DataQueryModal({ onClose }) {
  const [text, setText] = useState('');
  // ask -> reading -> answer. Drives the size, so it is one value rather than
  // several booleans that could disagree about how big the panel should be.
  const [stage, setStage] = useState('ask');
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [examples, setExamples] = useState([]);
  const [showSql, setShowSql] = useState(false);
  const inputRef = useRef(null);
  // Lazy initialiser, so the name is drawn once per mount rather than on every
  // render.
  const [askee] = useState(() => ASKEES[Math.floor(Math.random() * ASKEES.length)]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  useEffect(() => {
    inputRef.current?.focus();
    fetch(`${API_URL}/api/data-query/examples`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { examples: [] }))
      .then((d) => setExamples(d.examples || []))
      .catch(() => setExamples([]));
  }, []);

  const ask = useCallback(async (override) => {
    const question = String(override ?? text).trim();
    if (!question || busy) return;
    setBusy(true);
    setError('');
    setResult(null);
    setPlan(null);
    setShowSql(false);

    try {
      // Step one — what does it think was asked?
      const planRes = await fetch(`${API_URL}/api/data-query/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ question })
      });
      const planned = await planRes.json();
      if (!planRes.ok) { setError(planned.error || 'Could not read that question.'); return; }

      setPlan(planned);
      setStage('reading');

      if (!planned.answerable || !planned.token) return;

      // Step two — answer it. The reading is already on screen by now, which is
      // the whole reason these are separate calls.
      const runRes = await fetch(`${API_URL}/api/data-query/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token: planned.token })
      });
      const answered = await runRes.json();
      setResult(answered);
      if (!runRes.ok && !answered.reading) setError(answered.error || 'Could not answer that.');
      setStage('answer');
    } catch (_) {
      setError('Could not reach the server.');
    } finally {
      setBusy(false);
    }
  }, [text, busy]);

  const reset = useCallback(() => {
    setStage('ask'); setPlan(null); setResult(null); setError(''); setText('');
    setShowSql(false);
    inputRef.current?.focus();
  }, []);

  const conf = plan?.confidence ? CONFIDENCE[plan.confidence] : null;
  const rows = result?.rows || [];
  const columns = result?.columns || [];

  return ReactDOM.createPortal(
    <div className="dq-overlay" onMouseDown={onClose}>
      <div
        className={`dq-panel dq-stage-${stage}`}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Ask the Data"
      >
        {/* ── The prompt ─────────────────────────────────────────────────── */}
        <div className="dq-ask-row">
          <span className={`material-symbols-rounded dq-spark${busy ? ' spinning' : ''}`}>
            {busy ? 'progress_activity' : 'auto_awesome'}
          </span>
          <input
            ref={inputRef}
            className="dq-input"
            value={text}
            placeholder={`Ask ${askee} anything…`}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); ask(); }
            }}
            aria-label="Ask a question about your data"
          />
          {stage !== 'ask' && (
            <button className="dq-icon-btn" onClick={reset} title="Ask something else">
              <span className="material-symbols-rounded">refresh</span>
            </button>
          )}
          <button className="dq-icon-btn" onClick={onClose} title="Close">
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        {/* Examples, only while nothing has been asked. */}
        <Reveal open={stage === 'ask' && !busy && !error}>
          <div className="dq-examples">
            {examples.map((ex) => (
              <button key={ex} className="dq-example" onClick={() => { setText(ex); ask(ex); }}>
                {ex}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal open={Boolean(error)}>
          <p className="dq-error">
            <span className="material-symbols-rounded">error</span>
            {error}
          </p>
        </Reveal>

        {/* ── What it understood ─────────────────────────────────────────── */}
        <Reveal open={Boolean(plan?.reading)}>
          <div className="dq-reading">
            <div className="dq-reading-head">
              <span className="dq-reading-label">Reading</span>
              {conf && <span className={`dq-conf ${conf.cls}`}>{conf.label}</span>}
            </div>
            <p className="dq-reading-text">{plan?.reading}</p>

            {plan?.caveats?.length > 0 && (
              <ul className="dq-caveats">
                {plan.caveats.map((c, i) => (
                  <li key={i}>
                    <span className="material-symbols-rounded">info</span>
                    {c}
                  </li>
                ))}
              </ul>
            )}

            {plan && !plan.answerable && (
              <p className="dq-refusal">
                {plan.refusalReason === 'asks_to_change'
                  && 'This can only read data — it cannot change, create or send anything.'}
                {plan.refusalReason === 'not_in_data'
                  && 'That is not something the data covers.'}
                {plan.refusalReason === 'not_about_data'
                  && 'That is not a question about the business data.'}
                {!plan.refusalReason && 'That could not be answered.'}
              </p>
            )}
          </div>
        </Reveal>

        {/* ── The answer ─────────────────────────────────────────────────── */}
        <Reveal open={stage === 'answer' && Boolean(result)}>
          <div className="dq-answer">
            {result?.answered ? (
              <>
                {rows.length === 0 ? (
                  <p className="dq-empty">
                    No rows matched. That is an answer — there is nothing on record for this.
                  </p>
                ) : (
                  <ResultTable columns={columns} rows={rows} />
                )}

                <div className="dq-foot">
                  <span>
                    {rows.length} {rows.length === 1 ? 'row' : 'rows'}
                    {result.truncated && ' (capped)'}
                    {typeof result.ms === 'number' && ` · ${result.ms}ms`}
                    {result.repairs > 0 && ` · ${result.repairs} retr${result.repairs === 1 ? 'y' : 'ies'}`}
                  </span>
                  {result.sql && (
                    <button className="dq-link" onClick={() => setShowSql((s) => !s)}>
                      {showSql ? 'Hide query' : 'Show query'}
                    </button>
                  )}
                </div>

                <Reveal open={showSql}>
                  <pre className="dq-sql">{result.sql}</pre>
                </Reveal>
              </>
            ) : (
              <p className="dq-error">
                <span className="material-symbols-rounded">error</span>
                {result?.error || 'That could not be answered.'}
              </p>
            )}
          </div>
        </Reveal>
      </div>
    </div>,
    document.body
  );
}
